import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { date, money } from '../lib/format';
import { useAuth } from '../context/AuthContext';
import Drawer from '../components/Drawer';
import PageHeader from '../components/PageHeader';

const STATUS_META = [
  { key: 'to_be_scheduled', label: 'To be scheduled', tone: 'violet' },
  { key: 'waiting_parts', label: 'Waiting parts', tone: 'amber' },
  { key: 'in_process', label: 'In process', tone: 'blue' },
  { key: 'on_hold', label: 'On-hold', tone: 'slate' },
  { key: 'completed', label: 'Completed', tone: 'green' },
];
const STATUS_LABEL = Object.fromEntries(STATUS_META.map((s) => [s.key, s.label]));
const typeLabel = (t) => ({ sim_repair: 'Sim repair', sim_move: 'Simulator move', sim_upgrade: 'Simulator upgrade', site_visit: 'Site visit', flight: 'Flight', other: 'Other' }[t] || t);
const emptyTask = () => ({ title: '', customer: '', due_date: '', comments: '', status: 'in_process' });
const emptyPart = () => ({ part_name: '', sku: '', unit_price: '', quantity: '1', ordered_from: '', notes: '' });
const isOverdue = (d, status) => d && status !== 'completed' && new Date(d) < new Date(new Date().toDateString());

export default function KioskBoard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);

  // Drawer: editing/creating a task
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [form, setForm] = useState(emptyTask());
  const [parts, setParts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [partForm, setPartForm] = useState(emptyPart());
  const [editingPart, setEditingPart] = useState(null); // partId being edited
  const [kioskLink, setKioskLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api('/board')
      .then((d) => { setJobs(d.jobs || []); setTasks(d.tasks || []); })
      .catch(() => { setJobs([]); setTasks([]); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api('/board/kiosk-link').then(setKioskLink).catch(() => {}); }, []);

  const kioskUrl = kioskLink ? `${window.location.origin}${kioskLink.path}` : '';
  function copyKiosk() {
    if (!kioskUrl) return;
    navigator.clipboard?.writeText(kioskUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  }
  async function rotateKiosk() {
    if (!window.confirm('Issue a new display link? The current wall-display URL will stop working.')) return;
    try { setKioskLink(await api('/board/kiosk-link/rotate', { method: 'POST' })); } catch (e) { setNotice(e?.detail || 'Could not rotate the link.'); }
  }

  function openNew() {
    setTaskId(null); setForm(emptyTask()); setParts([]); setPartForm(emptyPart()); setEditingPart(null); setNotice(null); setOpen(true);
  }
  function openTask(t) {
    setTaskId(t.id);
    setForm({ title: t.title, customer: t.customer || '', due_date: t.due_date ? String(t.due_date).slice(0, 10) : '', comments: t.comments || '', status: t.status });
    setParts([]); setPartForm(emptyPart()); setEditingPart(null); setNotice(null); setOpen(true);
    api(`/board/tasks/${t.id}`).then((d) => setParts(d.parts || [])).catch(() => {});
  }
  function closeDrawer() { setOpen(false); }

  async function saveTask() {
    if (!form.title.trim()) { setNotice('Enter a task.'); return; }
    setSaving(true); setNotice(null);
    try {
      const body = { ...form, due_date: form.due_date || null };
      const saved = taskId
        ? await api(`/board/tasks/${taskId}`, { method: 'PUT', body })
        : await api('/board/tasks', { method: 'POST', body });
      setTaskId(saved.id);
      setParts(saved.parts || []);
      load();
      if (!taskId) setNotice('Saved — you can now add parts to order below.');
    } catch (e) { setNotice(e?.detail || 'Could not save the task.'); }
    finally { setSaving(false); }
  }
  async function deleteTask() {
    if (!taskId || !window.confirm('Delete this task?')) return;
    try { await api(`/board/tasks/${taskId}`, { method: 'DELETE' }); setOpen(false); load(); }
    catch (e) { setNotice(e?.detail || 'Could not delete the task.'); }
  }

  async function savePart() {
    if (!taskId) return;
    if (!partForm.part_name.trim()) { setNotice('Part name is required.'); return; }
    try {
      const d = editingPart
        ? await api(`/board/tasks/${taskId}/parts/${editingPart}`, { method: 'PUT', body: partForm })
        : await api(`/board/tasks/${taskId}/parts`, { method: 'POST', body: partForm });
      setParts(d.parts || []); setPartForm(emptyPart()); setEditingPart(null); load();
    } catch (e) { setNotice(e?.detail || 'Could not save the part.'); }
  }
  function editPart(p) {
    setEditingPart(p.id);
    setPartForm({ part_name: p.part_name, sku: p.sku || '', unit_price: p.unit_price ?? '', quantity: p.quantity ?? 1, ordered_from: p.ordered_from || '', notes: p.notes || '' });
  }
  async function toggleOrdered(p) {
    try { const d = await api(`/board/tasks/${taskId}/parts/${p.id}`, { method: 'PUT', body: { is_ordered: !p.is_ordered } }); setParts(d.parts || []); }
    catch (e) { setNotice(e?.detail || 'Could not update the part.'); }
  }
  async function deletePart(pid) {
    try { const d = await api(`/board/tasks/${taskId}/parts/${pid}`, { method: 'DELETE' }); setParts(d.parts || []); if (editingPart === pid) { setEditingPart(null); setPartForm(emptyPart()); } load(); }
    catch (e) { setNotice(e?.detail || 'Could not delete the part.'); }
  }

  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="board">
      <PageHeader
        title="My Board"
        subtitle={`${firstName ? `${firstName}'s ` : ''}jobs and personal task list.`}
        actions={<button className="btn btn--primary" onClick={openNew}>+ New task</button>}
      />

      {kioskLink && (
        <div className="kiosklink">
          <span className="kiosklink__lbl">Public wall display</span>
          <code className="kiosklink__url">{kioskUrl}</code>
          <button className="btn btn--sm" onClick={copyKiosk}>{copied ? 'Copied!' : 'Copy'}</button>
          <a className="btn btn--sm" href={kioskUrl} target="_blank" rel="noreferrer">Open</a>
          {user?.role === 'admin' && <button className="btn btn--sm btn--ghost" onClick={rotateKiosk}>New link</button>}
        </div>
      )}

      {/* Upcoming assigned jobs */}
      <section className="board__jobs">
        <h2 className="board__h2">Upcoming jobs assigned to me</h2>
        {loading ? <p className="hint">Loading…</p>
          : jobs.length === 0 ? <p className="hint">No upcoming jobs assigned to you.</p>
            : (
              <div className="jobstrip">
                {jobs.map((j) => (
                  <div className="jobstrip__card" key={j.id}>
                    <div className="jobstrip__top">
                      <span className="jobstrip__num">{j.job_number}</span>
                      {j.business_short && <span className="jobstrip__biz">{j.business_short}</span>}
                    </div>
                    <div className="jobstrip__cust">{j.customer_name || j.title || 'Job'}</div>
                    <div className="jobstrip__meta">{typeLabel(j.type)}</div>
                    {j.scheduled_date && <div className="jobstrip__meta">{date(j.scheduled_date)}</div>}
                    {j.location && <div className="jobstrip__meta">{j.location}</div>}
                  </div>
                ))}
              </div>
            )}
      </section>

      {/* Task board */}
      <section>
        <h2 className="board__h2">My tasks</h2>
        <div className="board__cols">
          {STATUS_META.map((col) => {
            const list = tasks.filter((t) => t.status === col.key);
            return (
              <div className="board__col" key={col.key}>
                <div className={`board__coltitle board__coltitle--${col.tone}`}>{col.label}<span className="board__count">{list.length}</span></div>
                {col.key === 'completed' && list.length > 0 && <div className="board__colnote">Auto-clears 3 days after completion</div>}
                <div className="board__cards">
                  {list.map((t) => (
                    <button className="tcard" key={t.id} onClick={() => openTask(t)}>
                      <div className="tcard__title">{t.title}</div>
                      {t.customer && <div className="tcard__cust">{t.customer}</div>}
                      <div className="tcard__meta">
                        {t.due_date && <span className={'tcard__due' + (isOverdue(t.due_date, t.status) ? ' tcard__due--over' : '')}>Due {date(t.due_date)}</span>}
                        {t.parts_to_order > 0 && <span className="tcard__parts">{t.parts_to_order} to order</span>}
                        {t.parts_to_order === 0 && t.part_count > 0 && <span className="tcard__parts tcard__parts--ok">parts ordered</span>}
                      </div>
                      {t.comments && <div className="tcard__note">{t.comments}</div>}
                    </button>
                  ))}
                  {list.length === 0 && <p className="board__empty">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Drawer open={open} title={taskId ? 'Task' : 'New task'} onClose={closeDrawer} width={560}
        footer={<>
          {taskId && <button className="btn btn--danger" onClick={deleteTask}>Delete</button>}
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={closeDrawer}>Close</button>
          <button className="btn btn--primary" onClick={saveTask} disabled={saving}>{saving ? 'Saving…' : 'Save task'}</button>
        </>}>
        {notice && <div className="form-note">{notice}</div>}
        <div className="field"><label>Task</label>
          <input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
        <div className="form-grid">
          <div className="field"><label>Customer</label><input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} /></div>
          <div className="field"><label>Date due</label><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
        </div>
        <div className="field"><label>Status</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {STATUS_META.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div className="field"><label>Comments</label>
          <textarea rows={3} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></div>

        <h3 className="form-section">Parts to order</h3>
        {!taskId ? <p className="hint">Save the task first to add parts to order.</p> : (
          <>
            {parts.length === 0 ? <p className="hint">No parts added.</p> : (
              <div className="bparts">
                {parts.map((p) => (
                  <div className={'bpart' + (p.is_ordered ? ' bpart--ordered' : '')} key={p.id}>
                    <label className="bpart__chk" title="Mark ordered">
                      <input type="checkbox" checked={!!p.is_ordered} onChange={() => toggleOrdered(p)} />
                    </label>
                    <div className="bpart__body">
                      <div className="bpart__name">{p.part_name}{p.sku ? <span className="bpart__sku"> · {p.sku}</span> : null}</div>
                      <div className="bpart__meta">{Number(p.quantity)} × {money(p.unit_price)} = {money(p.total)}{p.ordered_from ? ` · from ${p.ordered_from}` : ''}{p.is_ordered ? ' · ordered' : ''}</div>
                      {p.notes && <div className="bpart__notes">{p.notes}</div>}
                    </div>
                    <div className="bpart__acts">
                      <button className="btn btn--sm" onClick={() => editPart(p)}>Edit</button>
                      <button className="btn btn--sm btn--ghost" onClick={() => deletePart(p.id)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="bpartadd">
              <input placeholder="Part name" value={partForm.part_name} onChange={(e) => setPartForm({ ...partForm, part_name: e.target.value })} />
              <div className="form-grid">
                <input placeholder="SKU" value={partForm.sku} onChange={(e) => setPartForm({ ...partForm, sku: e.target.value })} />
                <input placeholder="Ordered from" value={partForm.ordered_from} onChange={(e) => setPartForm({ ...partForm, ordered_from: e.target.value })} />
              </div>
              <div className="form-grid">
                <input type="number" step="0.01" placeholder="Unit price" value={partForm.unit_price} onChange={(e) => setPartForm({ ...partForm, unit_price: e.target.value })} />
                <input type="number" step="1" placeholder="Qty" value={partForm.quantity} onChange={(e) => setPartForm({ ...partForm, quantity: e.target.value })} />
              </div>
              <input placeholder="Notes" value={partForm.notes} onChange={(e) => setPartForm({ ...partForm, notes: e.target.value })} />
              <div className="bpartadd__acts">
                <button className="btn btn--sm btn--primary" onClick={savePart}>{editingPart ? 'Update part' : 'Add part'}</button>
                {editingPart && <button className="btn btn--sm" onClick={() => { setEditingPart(null); setPartForm(emptyPart()); }}>Cancel</button>}
              </div>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
